<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Capacitor } from '@capacitor/core';
import useLocale from '../../composables/useLocale';
import useUiStore from '../../stores/ui';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faExpand, faCompress } from '@fortawesome/pro-solid-svg-icons';
import type { FontAwesomeIconSizes } from '../../types';
import { useDisplay } from 'vuetify';

library.add(faExpand, faCompress);

type Props = {
  showTooltips?: boolean;
  btnSize?: number;
  iconSize?: FontAwesomeIconSizes;
};

withDefaults(defineProps<Props>(), {
  showTooltips: true,
  btnSize: 48,
  iconSize: 'xl',
});

const { t } = useLocale();
const { smAndDown } = useDisplay();
const uiStore = useUiStore();
const { isFullscreen } = storeToRefs(uiStore);
const { toggleFullscreen } = uiStore;

const isNative = computed(() => Capacitor.isNativePlatform());
const isFullscreenSupported = computed(() => document.fullscreenEnabled !== undefined && document.fullscreenEnabled);
</script>

<template>
  <v-btn
    v-if="!isNative && isFullscreenSupported && !smAndDown"
    data-testid="bp-fullscreen-btn"
    color="on-brand"
    variant="text"
    :ripple="false"
    icon
    :aria-label="isFullscreen ? t('book_player.button_fullscreen_exit') : t('book_player.button_fullscreen')"
    :title="isFullscreen ? t('book_player.button_fullscreen_exit') : t('book_player.button_fullscreen')"
    :size="btnSize"
    @click="toggleFullscreen"
  >
    <FontAwesomeIcon :icon="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'" :size="iconSize" />
    <v-tooltip
      :disabled="!showTooltips"
      activator="parent"
      location="top"
      :text="isFullscreen ? t('book_player.button_fullscreen_exit') : t('book_player.button_fullscreen')"
    />
  </v-btn>
</template>
